import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Baby, Plus, Pencil, Trash2 } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { BabyPlan } from "./BabyPlan";

const billingToast = (b) => {
  if (!b) return;
  if (b.error) toast.error(b.error);
  else toast.message(`Your plan now has ${b.seats} seat${b.seats > 1 ? "s" : ""} (was ${b.previous}).`);
};

const inp = "px-4 py-2.5 rounded-xl bg-white border border-[#D6CDB4] text-sm text-[#2C4A3E] focus:outline-none focus:border-[#2C4A3E]";

const BabyForm = ({ baby, onSaved, onCancel }) => {
  const [v, setV] = useState({ name: baby?.name || "", birth_date: baby?.birth_date || "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = baby ? await http.put(`/babies/${baby.id}`, v) : await http.post("/babies", v);
      toast.success(baby ? "Saved" : `Welcome, ${r.data.name}!`);
      billingToast(r.data.billing);
      onSaved(r.data);
    }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={save} className="flex flex-wrap items-end gap-3 rounded-2xl bg-[#F7F3EB] border border-[#E6DEC3] p-4" data-testid="baby-form">
      <label className="text-xs text-stone-600">Baby's name<input required maxLength={40} value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} className={`${inp} block mt-1`} data-testid="baby-name-input" /></label>
      <label className="text-xs text-stone-600">Birth date<input required type="date" max={new Date().toISOString().slice(0, 10)} value={v.birth_date} onChange={(e) => setV({ ...v, birth_date: e.target.value })} className={`${inp} block mt-1`} data-testid="baby-birth-input" /></label>
      <button disabled={busy} data-testid="baby-save-btn" className="px-5 py-2.5 rounded-full bg-[#2C4A3E] text-white text-sm disabled:opacity-60">{baby ? "Save" : "Add baby"}</button>
      {onCancel && <button type="button" onClick={onCancel} data-testid="baby-cancel-btn" className="px-4 py-2.5 text-sm text-stone-600">Cancel</button>}
    </form>
  );
};

const LinkDaycare = ({ onLinked }) => {
  const [code, setCode] = useState("");
  const link = async (e) => {
    e.preventDefault();
    try { const r = await http.post("/babies/link", { code }); toast.success(`Linked to ${r.data.name} at ${r.data.daycare}`); setCode(""); onLinked(r.data.id); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={link} className="mt-4 flex flex-wrap items-center gap-2 text-sm" data-testid="baby-link-form">
      <span className="text-stone-600">Child at a daycare that uses ConsciousLearn?</span>
      <input required minLength={4} maxLength={12} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Code" className={`${inp} w-40 uppercase font-mono tracking-widest`} data-testid="baby-link-code" />
      <button className="px-4 py-2.5 rounded-full border border-[#2C4A3E] text-[#2C4A3E] hover:bg-white" data-testid="baby-link-btn">Link</button>
    </form>
  );
};

export const ConsciousBaby = () => {
  const [babies, setBabies] = useState(null);
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState(null);
  const load = (pick) => http.get("/babies").then((r) => { setBabies(r.data); setSel(pick || r.data[0]?.id || null); });
  useEffect(() => { load(); }, []);
  const remove = async (b) => {
    if (!window.confirm(b.linked ? `Unlink ${b.name} from ${b.daycare}? You can link again with the daycare's code.` : `Remove ${b.name}'s Conscious Baby profile? Activity history and journal entries will be deleted.`)) return;
    try { const r = await http.delete(`/babies/${b.id}`); toast.success(r.data.unlinked ? `Unlinked from ${b.daycare}` : "Removed"); billingToast(r.data.billing); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!babies) return <p className="mt-10 text-stone-500">Loading…</p>;
  const cur = babies.find((b) => b.id === sel);
  return (
    <section className="mt-10" data-testid="conscious-baby">
      <div className="rounded-[2rem] bg-[#EEF3EF] border border-[#C8DCCB] p-6 md:p-8">
        <p className="text-xs uppercase tracking-[0.25em] text-[#6B9080] font-semibold">Ages 0 to 3 · Parent-led</p>
        <h2 className="font-display text-3xl md:text-4xl text-[#2C4A3E] mt-2 flex items-center gap-3"><Baby className="w-8 h-8 text-[#6B9080]" />Conscious Baby</h2>
        <p className="mt-3 text-stone-600 max-w-2xl">Gentle daily moments to share with your little one: calm breathing, sensory play, songs, first words in English and Spanish, and movement. Each baby is one seat on the Parent plan (sibling discounts apply), or choose the baby-only plan at $9/month.</p>
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="baby-tabs">
          {babies.map((b) => <button key={b.id} onClick={() => { setSel(b.id); setMode(null); }} data-testid={`baby-tab-${b.id}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${sel === b.id ? "bg-[#2C4A3E] text-white" : "bg-white border border-[#C8DCCB] text-[#2C4A3E] hover:bg-[#F7F3EB]"}`}>{b.name} · {b.months} mo{b.linked ? ` · ${b.daycare}` : ""}</button>)}
          <button onClick={() => setMode(mode === "add" ? null : "add")} data-testid="baby-add-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-dashed border-[#6B9080] text-sm text-[#2C4A3E] hover:bg-white transition-colors"><Plus className="w-4 h-4" />Add baby</button>
          {cur && <span className="inline-flex items-center">
            {!cur.linked && <button onClick={() => setMode(mode === "edit" ? null : "edit")} data-testid="baby-edit-btn" aria-label="Edit baby" className="p-2 rounded-full hover:bg-white text-stone-600"><Pencil className="w-4 h-4" /></button>}
            <button onClick={() => remove(cur)} data-testid="baby-remove-btn" aria-label={cur.linked ? "Unlink baby" : "Remove baby"} title={cur.linked ? "Unlink from daycare" : "Remove"} className="p-2 rounded-full hover:bg-white text-stone-600"><Trash2 className="w-4 h-4" /></button>
          </span>}
        </div>
        {(mode === "add" || !babies.length) && <div className="mt-4"><BabyForm onSaved={(b) => { setMode(null); load(b.id); }} onCancel={babies.length ? () => setMode(null) : null} /></div>}
        <LinkDaycare onLinked={(id) => load(id)} />
        {mode === "edit" && cur && <div className="mt-4"><BabyForm baby={cur} onSaved={(b) => { setMode(null); load(b.id); }} onCancel={() => setMode(null)} /></div>}
      </div>
      {cur && <BabyPlan key={cur.id + cur.track_id + cur.birth_date} baby={cur} />}
    </section>
  );
};
